import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import useScript from './useScript'; // Import the custom hook

import { Icon } from '@controlrooms/components';

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 10;
  img {
    display: none;
  }
`;

interface MicrosoftTeamsShareWindow extends Window {
  shareToMicrosoftTeams?: {
    renderButtons: () => void;
  };
}

const TeamsShareButton = ({ link }: { link: string }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { isLoaded: isScriptLoaded, isError: isScriptError } = useScript(
    'https://teams.microsoft.com/share/launcher.js',
  );

  useEffect(() => {
    if (isScriptLoaded && buttonRef.current) {
      const teamsWindow = window as MicrosoftTeamsShareWindow;

      // Give the script a moment to initialize, then call renderButtons
      const timer = setTimeout(() => {
        if (teamsWindow.shareToMicrosoftTeams?.renderButtons) {
          teamsWindow.shareToMicrosoftTeams.renderButtons();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isScriptLoaded, link]);

  const handleClick = () => {
    if (!isScriptLoaded) {
      console.error('Teams launcher script not loaded yet.');
      return;
    }
    if (isScriptError) {
      console.error('Failed to load Teams launcher script.');
      return;
    }

    // Manual fallback: open Teams share popup directly
    const encodedUrl = encodeURIComponent(link);
    const teamsShareUrl = `https://teams.microsoft.com/share?href=${encodedUrl}`;
    window.open(
      teamsShareUrl,
      'Share to Teams',
      'width=700,height=600,menubar=no,toolbar=no,location=no',
    );
  };

  return (
    <StyledButton
      ref={buttonRef}
      type="button"
      onClick={handleClick}
      data-testid="ms-teams-share-popup"
      className="teams-share-button"
      data-icon-px-size="124"
      data-href={link}
      role="button"
      aria-label="Share via Microsoft Teams"
      disabled={!isScriptLoaded && !isScriptError}
    >
      <Icon name="teams" />
      Share via Teams
    </StyledButton>
  );
};

export default TeamsShareButton;
